import { useQuery, useMutation } from "@apollo/react-hooks";
import { useParams } from "react-router-dom";
import gql from "graphql-tag";
import Permissions from "./../../configs/permissions";
import usePermissions from "./../../hooks/permissions";
import { clientPromotedOffers } from "./../../graphql/queries/benefitQueries";
import { updateClientPromotedOffersMutation } from "./../../graphql/mutations/benefitMutations";
import useDataProvider from "./../../hooks/dataProvider";
import useFormErrors from "./../../hooks/formErrors";
import useNotification from "../../hooks/notifications";
import {
  AddButton,
  DeleteButton,
  FormControls,
  SaveButton,
} from "./../../mood-ui/Controls";
import { SelectField, MultiSelectField } from "./../../mood-ui/Fields";
import {
  BlockContainer,
  Col,
  FormContainer,
  FormData,
  PageContainer,
  Row,
} from "./../../mood-ui/Layout";
import { Heading } from "./../../mood-ui/Typography";
import React, { useState } from "react";
import {
  TableContainer,
  TableView,
  TColBorderless,
  TContent,
  THCol,
  THeading,
  TRow,
} from "./../../mood-ui/DataTable";
import { SuccessNotification } from "../../mood-ui/Notifications";
import PageSpinner from "../../mood-ui/PageSpinner";

const CLIENT_PROMOTED_OFFERS = gql(clientPromotedOffers);
const UPDATE_CLIENT_PROMOTED_OFFERS = gql(updateClientPromotedOffersMutation);
//I just was lazy to ad a param here. So just duplicated the whole component.
const GlobalPromotedOffers = () => {
  const { protectPage } = usePermissions();
  protectPage(Permissions.Mutation.UpdateOffer);
  const { resolveMutationErrors } = useFormErrors();

  const id = 0;

  const {
    notify: displayNotification,
    notificationState,
    handleClose,
  } = useNotification();

  const [activeOffersForSelectable] = useDataProvider(
    "activeOffersForSelectable",
  );
  const [labels] = useDataProvider("labels");

  const [promotedOffers, setPromotedOffers] = useState([]);

  const { loading } = useQuery(CLIENT_PROMOTED_OFFERS, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    onCompleted: (result) => {
      if (!result || !result.clientPromotedOffers) return;
      setPromotedOffers(
        result.clientPromotedOffers.offers.map(({ offer_id, label_ids }) => ({
          offer_id,
          label_ids,
        })),
      );
    },
    onError: (e) => {
      console.log("e", e);
    },
  });

  const [updateClientPromotedOffers, { loading: isUpdating }] = useMutation(
    UPDATE_CLIENT_PROMOTED_OFFERS,
    {
      onCompleted: (result) => {
        displayNotification({
          heading: "Setup updated",
          message: "Successfully updated client promoted offers",
          secondsToDisplay: 1,
        });
      },
      onError: (e) => {
        resolveMutationErrors(e);
      },
    },
  );

  const handleAddOffer = () =>
    setPromotedOffers((prev) => [...prev, { offer_id: null, label_ids: [] }]);

  const handleUpdateOfferField = (index, offerId) => {
    setPromotedOffers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], offer_id: offerId };
      return updated;
    });
  };

  const handleUpdateLabelsField = (index, values) => {
    setPromotedOffers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], label_ids: values };
      return updated;
    });
  };

  const handleDeleteOffer = (index) =>
    setPromotedOffers((prev) => prev.filter((_, i) => i !== index));

  const handleSave = () => {
    const offers = promotedOffers.map((item) => ({
      offer_id: parseInt(item.offer_id, 10),
      label_ids: Array.isArray(item.label_ids)
        ? item.label_ids.map((lid) => parseInt(lid, 10))
        : [],
    }));

    updateClientPromotedOffers({
      variables: {
        client_id: parseInt(id, 10),
        offers,
      },
    });
  };

  if (!promotedOffers || !labels || !activeOffersForSelectable || loading)
    return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text={"Setup promoted offers"}
              description={
                "Assign offers that should be promoted for all clients users if not set on client level. "
              }
            />
          </Col>
        </Row>
        <FormContainer>
          <FormData>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Promoted Offers"
                      description="Assign promoted offers and labels for all clients users if not set on client level."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row tweaks="gap-0 sm:grid-cols-1 py-0 px-px">
                  <Col width={12} extraStyles="sm:col-span-1">
                    <TableContainer tweaks="relative mt-0">
                      <TableView>
                        <THeading>
                          <TRow>
                            <THCol tweaks="text-left" value="Offer" />
                            <THCol tweaks="text-left" value="Labels" />
                            <THCol width="w-[38.5px]" tweaks="px-0.5 py-0.5">
                              <AddButton
                                label="Add Offer"
                                onClick={handleAddOffer}
                                icon
                              />
                            </THCol>
                          </TRow>
                        </THeading>
                        <TContent>
                          {promotedOffers.map((item, index) => (
                            <TRow key={index}>
                              <TColBorderless tweaks="px-0.5 py-0.5">
                                <SelectField
                                  label=""
                                  value={item.offer_id}
                                  values={activeOffersForSelectable}
                                  onChange={(e) =>
                                    handleUpdateOfferField(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  valueKey="id"
                                  labelKey="admin_title"
                                  prependOption="Select Offer"
                                  isRequired={false}
                                  borderless={true}
                                />
                              </TColBorderless>
                              <TColBorderless
                                tweaks="px-0.5 py-0.5"
                                truncate={false}
                              >
                                <MultiSelectField
                                  label=""
                                  value={item.label_ids}
                                  values={labels}
                                  onChange={(e) =>
                                    handleUpdateLabelsField(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  valueKey="id"
                                  labelKey="name"
                                  prependOption="Select Label(s)"
                                  isRequired={false}
                                  borderless={true}
                                />
                              </TColBorderless>
                              <TColBorderless
                                width="w-[38.5px]"
                                tweaks="px-0.5 py-0.5"
                              >
                                <DeleteButton
                                  icon
                                  onClick={() => handleDeleteOffer(index)}
                                />
                              </TColBorderless>
                            </TRow>
                          ))}
                        </TContent>
                      </TableView>
                    </TableContainer>
                  </Col>
                </Row>
              </Col>
            </Row>
          </FormData>
          <FormControls>
            <SaveButton onClick={handleSave} loading={isUpdating} />
          </FormControls>
        </FormContainer>
      </BlockContainer>
      <SuccessNotification {...notificationState} onClose={handleClose} />
    </PageContainer>
  );
};

export default GlobalPromotedOffers;
